// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import { MainPanel } from "types";
import { Report } from "shared/types/cloudtypes";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionCreateReport } from "actions";
import { IReportState } from "states/guistates";
import { tabClickHandler } from "guiutils";

import FormattedDate from "view/comp/FormattedDate";
import FormattedTime from "view/comp/FormattedTime";

import { RoutedProps, withRouter } from "view/reactrouterinterceptor";

import IconEdit from "icons/iconEdit";

interface IReportEntryExtProps {
  reportId: number;
  isReadOnly: boolean;
}

interface IReportEntryProps {
  report: Report | undefined;
  reportLink: IReportState | undefined;
  isReportTimedout: boolean;
}

const mapStateToProps = (state: IState, ownProps: IReportEntryExtProps) => {
  const REQ_TIMEOUT = 15 * 60 * 1000; // 15 min
  const reportLink = state.gui.reportLinks[ownProps.reportId] ?? undefined;
  const isReportTimedout =
    isSet(reportLink?.requested) && Date.now() - REQ_TIMEOUT >= reportLink!.requested;

  const props: IReportEntryProps = {
    report: state.site.reportList?.find((curItem) => curItem.id === ownProps.reportId),
    reportLink,
    isReportTimedout,
  };
  return props;
};

interface IReportsDispatch {
  onEditReport: (reportId: number) => void;
  onCreateReport: (reportId: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IReportsDispatch = {
    onEditReport: (reportId: number) => {
      dispatch(raiseAction(ActionType.ACT_EDIT_REPORT, reportId));
    },
    onCreateReport: (reportId: number) => {
      const payload: IActionCreateReport = {
        reportId,
        previewOnly: false,
      };
      dispatch(raiseAction(ActionType.ACT_CREATE_REPORT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgEdit: {
    id: "edit report",
  },
});

class ReportsComponent extends React.Component<
  IReportEntryProps & IReportEntryExtProps & IReportsDispatch & RoutedProps
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const LINK_TIMEOUT = 24 * 3600 * 1000; // 1 day
    const isReportRequested =
      isSet(this.props.reportLink?.requested) &&
      Date.now() - LINK_TIMEOUT < this.props.reportLink!.requested;
    // extract timestamp from name (format: path/name_timestamp.suffix)
    const linkParts = (this.props.reportLink?.link ?? "").split(/[_.]/);
    const reportTimestamp = parseInt(linkParts[linkParts.length - 2] ?? "");
    const isReportValid = Date.now() - LINK_TIMEOUT < reportTimestamp;

    return (
      <tr>
        <td>
          <span className={!this.props.report?.isActive ? "deactivated" : ""}>
            {this.props.report?.title}
          </span>
        </td>
        <td>
          {!this.props.isReadOnly && (
            <span
              className="img-link"
              tabIndex={0}
              onKeyDown={tabClickHandler}
              onClick={() => this.props.onEditReport(this.props.reportId)}
              title={this.context.formatMessage(messages.msgEdit)}
            >
              <IconEdit />
            </span>
          )}
        </td>
        <td>
          {this.props.report?.isActive && (
            <Button
              variant="outline-success"
              className="mb-1"
              onClick={(e) =>
                this.props.navigate(`../${MainPanel.REPORTDATAVIEW}?report=${this.props.reportId}`)
              }
            >
              <FormattedMessage id="View Report Online" />
            </Button>
          )}
        </td>
        <td>
          {this.props.report?.isActive && (
            <Button
              variant="outline-success"
              className="mb-1"
              onClick={(e) => this.props.onCreateReport(this.props.reportId)}
            >
              {isReportValid ?
                <FormattedMessage id="Update Report File" />
              : <FormattedMessage id="Create Report File" />}
            </Button>
          )}
        </td>
        <td>
          {this.props.report?.isActive && (
            <>
              {isReportValid && (
                <a
                  href={this.props.reportLink?.link}
                  target="_blank"
                  rel="noreferrer"
                  className="me-2"
                >
                  <FormattedMessage id="Download" /> (<FormattedDate valueMs={reportTimestamp} />{" "}
                  <FormattedTime valueMs={reportTimestamp} />)
                </a>
              )}
              {!isReportValid &&
                isReportRequested &&
                (isSet(this.props.reportLink?.error) ?
                  <span className="error">{this.props.reportLink?.error}</span>
                : this.props.isReportTimedout ?
                  <span className="error">
                    <FormattedMessage id="Report generation timed out. Try reducing report content." />
                  </span>
                : <div className="spinner">
                    <FormattedMessage id="generating report" />
                    <div id="loading-spinner" />
                  </div>)}
            </>
          )}
        </td>
      </tr>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ReportsComponent));
